import React, { useEffect, useState } from "react";
import FormInputs from "../../../../../../components/UI/FormInputs";
import { getLookupByKey, isObject, isSet } from "../../../../../../utils/commonUtils";
import TableGrid from "../../../../../../components/UI/TableGrid";
import WithRoutify from "../../../../../../hoc/WithRoutify";
import SectionLoader from "../../../../../../components/UI/SectionLoader";
import PatientChartsService from "../../../../../../services/PatientChartsService";
import LabOrderdService from "../../../../../../services/LabOrderdService";
import Buttons from "../../../../../../components/UI/Buttons";
import AddLabPanel from "./AddLabPanel";

const LabPanel = (props) => {
    const { Toast, Loader,fetchRecords, setChildLab, childLab, updateFormData} = props;
    const parent_id=props.formData.id;
    const [panelList, setPanelList] = useState({ tableHeaders: [], tableRows: { data: [] } });
    const [isLoading, setLoaded] = useState(false);
    const [validation, setValidation] = useState({});
    const [laborderOptions, setLabOrderOptions] = useState([]);
    const [formData, setFormData] = useState([]);
    const [isModalOpen,setModalToogle]  = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [inputFields, setInputFields] = useState([]);
    const [hasConfiguredChildLabOrders, setHasConfiguredChildLabOrders] = useState(false);
    const [childLabConfigs, setChildLabConfigs] = useState([]);
    const [modifiedRowIds, setModifiedRowIds] = useState(new Set());
    
    // Helper function to extract error message from API response
    const getErrorMessage = (response) => {
        if (!response || !response.data) {
            return 'Failed to save';
        }
        
        // If data is a string, return it directly
        if (typeof response.data === 'string') {
            return response.data;
        }
        
        // If data is an object, extract error messages from validation errors
        if (isObject(response.data)) {
            const errorMessages = [];
            
            // Extract error messages from validation fields (like lab_order, etc.)
            for (let key in response.data) {
                if (key !== 'existing_records' && Array.isArray(response.data[key])) {
                    errorMessages.push(...response.data[key]);
                } else if (key !== 'existing_records' && typeof response.data[key] === 'string') {
                    errorMessages.push(response.data[key]);
                }
            }
            
            // If we have existing records, add info about them including lab order name
            if (response.data.existing_records && Array.isArray(response.data.existing_records)) {
                const recordCount = response.data.existing_records.length;
                if (recordCount > 0) {
                    // Get the lab order name from the first existing record
                    const labOrderName = response.data.existing_records[0]?.lab_order || 'this lab order';
                    errorMessages.push(`Found ${recordCount} existing record(s) for "${labOrderName}".`);
                }
            }
            
            // Format error messages with bullet points if multiple, single line if one
            if (errorMessages.length === 0) {
                return 'Failed to save';
            } else if (errorMessages.length === 1) {
                return errorMessages[0];
            } else {
                // Return as array so Toast component can render each as separate line
                return errorMessages.map(msg => `• ${msg}`);
            }
        }
        
        return 'Failed to save';
    };
    
    useEffect(() => {
        fetchLabOrder();
    }, []);
    useEffect(() => {
        if (parent_id) {
            fetchPanelList();
        } else {
            // For new entries, initialize with childLab if provided
            if (childLab && Array.isArray(childLab) && childLab.length > 0) {
                setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: childLab } });
            } else {
                setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: [] } });
            }
        }
    }, [parent_id]);
    
    // Fetch and auto-populate child lab order configurations when lab_order_id changes (for new entries)
    useEffect(() => {
        const fetchChildLabOrderConfigs = async () => {
            if (!parent_id && props.formData.lab_id && props.formData.lab_order_id) {
                try {
                    const configs = await LabOrderdService.getChildLabOrderConfigurations(
                        null, props.formData.lab_id, props.formData.lab_order_id
                    );
                    
                    if (configs && configs.length > 0) {
                        // Set flag that configured child lab orders exist
                        setHasConfiguredChildLabOrders(true);
                        
                        // Sort configurations by display_position
                        const sortedConfigs = [...configs].sort((a, b) => {
                            const posA = a.display_position || 999;
                            const posB = b.display_position || 999;
                            return posA - posB;
                        });
                        
                        // Store configs for inline editing
                        setChildLabConfigs(sortedConfigs);
                        
                        // Helper function to extract data from new API format
                        const extractFieldData = (config, fieldPrefix) => {
                            const dataValueKey = `${fieldPrefix}_data_value`;
                            
                            if (config[dataValueKey] && typeof config[dataValueKey] === 'object') {
                                const jsonData = config[dataValueKey];
                                const options = Array.isArray(jsonData.options) ? jsonData.options : (jsonData.options ? [jsonData.options] : []);
                                const value = jsonData.value || '';
                                const rangeArr = Array.isArray(jsonData.range) ? jsonData.range : [];
                                const paired = rangeArr.length > 0 ? rangeArr : null;
                                const min = rangeArr.length === 1 ? rangeArr[0].min : null;
                                const max = rangeArr.length === 1 ? rangeArr[0].max : null;
                                return {
                                    options,
                                    value,
                                    min: min || jsonData.min || null,
                                    max: max || jsonData.max || null,
                                    paired
                                };
                            }
                            
                            return {
                                options: [],
                                value: '',
                                min: null,
                                max: null,
                                paired: null
                            };
                        };
                        
                        // Helper: when dataType is 'none', use '--' for display
                        const getFieldValue = (config, fieldPrefix, data, defaultVal = '') => {
                            const dataType = config[`${fieldPrefix}_data_type`];
                            if (dataType === 'none') return '--';
                            return data.value || defaultVal;
                        };
                        
                        // Auto-populate child lab orders based on configurations (sorted by display_position)
                        const childLabData = sortedConfigs.map((config) => {
                            const resultData = extractFieldData(config, 'result');
                            const referenceRangeData = extractFieldData(config, 'reference_range');
                            const unitsData = extractFieldData(config, 'units');
                            const flagData = extractFieldData(config, 'flag');
                            const labMessageData = extractFieldData(config, 'lab_message');
                            
                            // Format reference_range display value
                            let referenceRangeDisplay = '';
                            const refDataType = config.reference_range_data_type || '';
                            if (refDataType === 'none') {
                                referenceRangeDisplay = '--';
                            } else if (refDataType === 'range' || (referenceRangeData.min != null && referenceRangeData.max != null && !referenceRangeData.paired)) {
                                referenceRangeDisplay = `${referenceRangeData.min || ''} - ${referenceRangeData.max || ''}`;
                            } else if (refDataType === 'reference_range' || refDataType === 'custom_referance_range') {
                                const rangeArr = referenceRangeData.paired || [];
                                if (rangeArr.length > 0) {
                                    referenceRangeDisplay = rangeArr.map(p => 
                                        p.name ? `${p.name}: ${p.min || ''}-${p.max || ''}` : `${p.min || ''}-${p.max || ''}`
                                    ).join(', ');
                                } else {
                                    referenceRangeDisplay = referenceRangeData.value || '';
                                }
                            } else {
                                referenceRangeDisplay = referenceRangeData.value || '';
                            }
                            
                            const rowId = `temp_${Math.random().toString(36).substring(2, 11)}`;
                            const childLab = {
                                lab_order: config.lab_order,
                                result: getFieldValue(config, 'result', resultData),
                                reference_range: referenceRangeDisplay,
                                units: getFieldValue(config, 'units', unitsData),
                                flag: getFieldValue(config, 'flag', flagData),
                                lab_message: getFieldValue(config, 'lab_message', labMessageData),
                                rowId: rowId // Store rowId in data for easy access
                            };
                            const fullItem = {
                                rowId: rowId,
                                data: {
                                    ...childLab,
                                    action: [
                                        {
                                            className: "btn btn-icon btn-transparent-dark",
                                            iconType: "Edit",
                                            title: "View",
                                            acl: "company-edit",
                                            clickHandler: () => editHandler(null, fullItem)
                                        },
                                        {
                                            className: 'btn btn-icon btn-transparent-dark',
                                            iconType: 'Remove',
                                            acl: 'any',
                                            title: 'Delete',
                                            isDelete: true,
                                            clickHandler: () => deleteHandler(rowId),
                                        }
                                    ]
                                }
                            };
                            return fullItem;
                        });
                        
                        // Update both local state and parent state
                        setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: childLabData } });
                        if (setChildLab) {
                            setChildLab(childLabData);
                        }
                        // Also update formData with child_lab for saving
                        if (updateFormData) {
                            const childLabPayload = childLabData.map(item => {
                                const data = item.data || item;
                                const { action, ...rest } = data;
                                return {
                                    lab_order: rest.lab_order || '',
                                    result: rest.result || '',
                                    reference_range: rest.reference_range || '',
                                    units: rest.units || '',
                                    flag: rest.flag || '',
                                    lab_message: rest.lab_message || ''
                                };
                            });
                            updateFormData({ child_lab: childLabPayload });
                        }
                    } else {
                        // No configurations found - allow manual adding
                        setHasConfiguredChildLabOrders(false);
                        setChildLabConfigs([]);
                        // Clear child lab if no configurations found
                        setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: [] } });
                        if (setChildLab) {
                            setChildLab([]);
                        }
                        if (updateFormData) {
                            updateFormData({ child_lab: [] });
                        }
                    }
                } catch (error) {
                    console.error('Error fetching child lab order configurations:', error);
                    setHasConfiguredChildLabOrders(false);
                    setChildLabConfigs([]);
                    setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: [] } });
                    if (setChildLab) {
                        setChildLab([]);
                    }
                    if (updateFormData) {
                        updateFormData({ child_lab: [] });
                    }
                }
            } else if (!parent_id && !props.formData.lab_order_id) {
                // Clear child lab if lab order is cleared
                setHasConfiguredChildLabOrders(false);
                setChildLabConfigs([]);
                setModifiedRowIds(new Set());
                setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: [] } });
                if (setChildLab) {
                    setChildLab([]);
                }
                if (updateFormData) {
                    updateFormData({ child_lab: [] });
                }
            }
        };
        
        fetchChildLabOrderConfigs();
    }, [props.formData.lab_order_id, props.formData.lab_id, parent_id]);
    const fetchLabOrder = async () => {
        // await props.fetchLabOrderList(null, null, (res) => {
        //     res.unshift({label:"-Select-",value:''})
        //     setLabOrderOptions(res);
        // });
        const response=await getLookupByKey('child_laborder');
        setLabOrderOptions(response);
    }
    const fetchPanelList = async () => {
        try {
            setLoaded(true);
            const response = await PatientChartsService.getChildLabOrders(parent_id);
            const itemsWithConfigs = response || [];
            
            // Check if there are configured child lab orders for existing entries
            const labId = props.formData.lab_id;
            const labOrderId = props.formData.lab_order_id;
            if (labId && labOrderId) {
                try {
                    const configs = await LabOrderdService.getChildLabOrderConfigurations(
                        null, labId, labOrderId
                    );
                    if (configs && configs.length > 0) {
                        const sortedConfigs = [...configs].sort((a, b) => {
                            const posA = a.display_position || 999;
                            const posB = b.display_position || 999;
                            return posA - posB;
                        });
                        setChildLabConfigs(sortedConfigs);
                        setHasConfiguredChildLabOrders(true);
                    } else {
                        setChildLabConfigs([]);
                        setHasConfiguredChildLabOrders(false);
                    }
                } catch (error) {
                    console.error('Error checking configured child lab orders:', error);
                    setChildLabConfigs([]);
                    setHasConfiguredChildLabOrders(false);
                }
            } else {
                setChildLabConfigs([]);
                setHasConfiguredChildLabOrders(false);
            }
            
            // String fields only
            const tableData = itemsWithConfigs.map((item) => ({
                rowId: item.id,
                data: {
                    id: item.id,
                    lab_order: item.lab_order || '',
                    result: item.result || '',
                    reference_range: item.reference_range || '',
                    units: item.units || '',
                    flag: item.flag || '',
                    lab_message: item.lab_message || '',
                    action: [
                        {
                            className: "btn btn-icon btn-transparent-dark",
                            iconType: "Edit",
                            title: "View",
                            acl: "company-edit",
                            clickHandler: () => editHandler(item.id, item)
                        },
                        {
                            className: 'btn btn-icon btn-transparent-dark',
                            iconType: 'Remove',
                            acl: 'any',
                            title: 'Delete',
                            isDelete: true,
                            clickHandler: () => deleteHandler(item.id),
                        }
                    ]
                }
            }));

            setPanelList({ tableHeaders: getTableHeaders(), tableRows: { data: tableData } });
            setLoaded(false);
        } catch (error) {
            setLoaded(false);
            console.error("Error fetching additional documents:", error);
        }
    }
    const getTableHeaders = () => [
        { label: 'Lab Ordered', key: 'lab_order', type: '' },
        { label: 'Result', key: 'result', type: '' },
        { label: 'Reference Range', key: 'reference_range', type: '' },
        { label: 'Units', key: 'units', type: '' },
        { label: 'Flag', key: 'flag', type: '' },
        { label: 'Lab Message', key: 'lab_message', type: '' },
        { label: 'Action', key: 'action', type: 'Actions' },
        // Add more columns as needed
    ];

    const submiHandler = async () => {
        // For new entries (no parent_id), update local state and parent state
        if (!parent_id) {
            const rowId = formData.rowId || `temp_${Math.random().toString(36).substring(2, 11)}`;
            const childLabData = {
                lab_order: formData.lab_order || '',
                result: formData.result || '',
                reference_range: formData.reference_range || '',
                units: formData.units || '',
                flag: formData.flag || '',
                lab_message: formData.lab_message || '',
                rowId: rowId // Store rowId in data for easy access
            };
            const updatedChildLab = {
                rowId: rowId,
                data: {
                    ...childLabData,
                    action: [
                        {
                            className: "btn btn-icon btn-transparent-dark",
                            iconType: "Edit",
                            title: "View",
                            acl: "company-edit",
                            clickHandler: () => editHandler(null, updatedChildLab)
                        },
                        {
                            className: 'btn btn-icon btn-transparent-dark',
                            iconType: 'Remove',
                            acl: 'any',
                            title: 'Delete',
                            isDelete: true,
                            clickHandler: () => deleteHandler(rowId),
                        }
                    ]
                }
            };
            
            let updatedPanelList = { ...panelList };
            if (formData.rowId) {
                // Update existing row - find by rowId
                updatedPanelList.tableRows.data = panelList.tableRows.data.map(item => 
                    item.rowId === formData.rowId ? updatedChildLab : item
                );
            } else {
                // Add new row
                updatedPanelList.tableRows.data = [...panelList.tableRows.data, updatedChildLab];
            }
            
            setPanelList(updatedPanelList);
            if (setChildLab) {
                setChildLab(updatedPanelList.tableRows.data);
            }
            // Update formData with child_lab for saving
            if (updateFormData) {
                const childLabPayload = updatedPanelList.tableRows.data.map(item => {
                    const data = item.data || item;
                    const { action, ...rest } = data;
                    return {
                        lab_order: rest.lab_order || '',
                        result: rest.result || '',
                        reference_range: rest.reference_range || '',
                        units: rest.units || '',
                        flag: rest.flag || '',
                        lab_message: rest.lab_message || ''
                    };
                });
                updateFormData({ child_lab: childLabPayload });
            }
            
            Toast.success(`Lab Panel ${formData.rowId ? 'Updated' : 'Added'} Successfully`);
            setModalToogle(false);
            setFormData({});
            setValidation({});
            if (fetchRecords) {
                fetchRecords();
            }
            return;
        }
        
        // For existing entries (with parent_id), save to backend
        Loader();
        let response;
        let updateValidation = {};

        const payload = {
            parent_lab_id: parent_id,
            lab_order: formData.lab_order || '',
            result: formData.result || '',
            reference_range: formData.reference_range || '',
            units: formData.units || '',
            flag: formData.flag || '',
            lab_message: formData.lab_message || ''
        };

        if (formData.id) {
            response = await PatientChartsService.updateChildLabOrders(formData.id, payload);
        } else {
            response = await PatientChartsService.saveChildLabOrders(payload);
        }
        Loader(false);
        // console.log('response',response);
        if (response && response.status && response.status == "ERROR") {
            if (isObject(response.data)) {
                for (let key in response.data) {
                    if (key !== 'existing_records') {
                        updateValidation = {
                            ...updateValidation,
                            [key]: Array.isArray(response.data[key]) 
                                ? response.data[key].join(", ") 
                                : response.data[key],
                        };
                    }
                }
                // Show error message in toast
                const errorMessage = getErrorMessage(response);
                Toast.error(errorMessage, 5000);
            } else {
                Toast.error(response.data || 'Failed to save', 5000);
                return false;
            }
            if (Object.keys(updateValidation).length > 0) {
                setValidation(updateValidation);
                return false;
            }
        }
        let message=`Lab Panel ${formData.id?'Updated':'Added'} Successfully`
        Toast.success(message);
        setModalToogle(false);
        fetchPanelList();
        fetchRecords();
        setFormData({});
        setValidation({});
    }
    const deleteHandler = async (id) => {
        // For new entries (no parent_id), remove from local state
        if (!parent_id) {
            const updatedPanelList = {
                ...panelList,
                tableRows: {
                    data: panelList.tableRows.data.filter(item => item.rowId !== id)
                }
            };
            setPanelList(updatedPanelList);
            if (setChildLab) {
                setChildLab(updatedPanelList.tableRows.data);
            }
            // Update formData with child_lab for saving
            if (updateFormData) {
                const childLabPayload = updatedPanelList.tableRows.data.map(item => {
                    const data = item.data || item;
                    const { action, ...rest } = data;
                    return {
                        lab_order: rest.lab_order || '',
                        result: rest.result || '',
                        reference_range: rest.reference_range || '',
                        units: rest.units || '',
                        flag: rest.flag || '',
                        lab_message: rest.lab_message || ''
                    };
                });
                updateFormData({ child_lab: childLabPayload });
            }
            Toast.success(`Lab Panel Deleted Successfully`);
            if (fetchRecords) {
                fetchRecords();
            }
            return;
        }
        
        // For existing entries, delete from backend
        const response=await PatientChartsService.deleteChildLabOrders(id);
        fetchPanelList();
        fetchRecords();
        Toast.success(`Lab Panel Deleted Successfully`);
    }
    const editHandler = async (id, item) => {
        setValidation({});
        const itemData = item?.data || item || {};
        const labOrderName = itemData.lab_order || '';
        
        // Get rowId from item structure or itemData
        const rowId = item?.rowId || itemData.rowId || null;
        
        // Set form data first
        setFormData({
            id: id || itemData.id,
            rowId: rowId,
            lab_order: labOrderName,
            result: itemData.result || '',
            reference_range: itemData.reference_range || '',
            units: itemData.units || '',
            flag: itemData.flag || '',
            lab_message: itemData.lab_message || ''
        });
        
        // Fetch configuration for this lab order
        const labId = props.formData.lab_id;
        const labOrderId = props.formData.lab_order_id;
        
        if (labId && labOrderId && labOrderName) {
            try {
                const configs = await LabOrderdService.getChildLabOrderConfigurations(
                    null, labId, labOrderId
                );
                
                // Find the configuration that matches the lab_order name
                const matchingConfig = configs?.find(config => config.lab_order === labOrderName);
                
                if (matchingConfig) {
                    setSelectedConfig(matchingConfig);
                    // Build input fields based on configuration - use current laborderOptions
                    const fields = buildInputFieldsFromConfig(matchingConfig, laborderOptions);
                    setInputFields(fields);
                } else {
                    // No matching config, use default fields - use current laborderOptions
                    setSelectedConfig(null);
                    setInputFields(getDefaultInputFields(laborderOptions));
                }
            } catch (error) {
                console.error('Error fetching configuration:', error);
                setSelectedConfig(null);
                setInputFields(getDefaultInputFields(laborderOptions));
            }
        } else {
            setSelectedConfig(null);
            setInputFields(getDefaultInputFields(laborderOptions));
        }
        
        setModalToogle(true);
    }
    
    const getDefaultInputFields = (options = laborderOptions) => [
        {
            type: "SelectList",
            options: options,
            label: 'Lab Order',
            name: 'lab_order',
            placeholder: 'Lab Order',
            required: true,
        },
        { type: 'TextInput', label: 'Result', name: 'result', placeholder: 'Result' },
        { type: 'TextInput', label: 'Reference Range', name: 'reference_range', placeholder: 'Reference Range' },
        { type: 'TextInput', label: 'Units', name: 'units', placeholder: 'Units' },
        { type: 'TextInput', label: 'Flag', name: 'flag', placeholder: 'Flag' },
        { type: 'TextArea', label: 'Lab Message', name: 'lab_message', placeholder: 'Lab Message' },
    ];
    
    const buildInputFieldsFromConfig = (config, options = laborderOptions) => {
        const fields = [
            {
                type: "SelectList",
                options: options,
                label: 'Lab Order',
                name: 'lab_order',
                placeholder: 'Lab Order',
                required: true,
            }
        ];
        
        // Helper to extract data value
        const getDataValue = (fieldPrefix) => {
            const dataValueKey = `${fieldPrefix}_data_value`;
            return config[dataValueKey] || {};
        };
        
        // Result field
        const resultDataType = config.result_data_type || 'textinput';
        const resultData = getDataValue('result');
        fields.push(buildFieldByType('result', 'Result', resultDataType, resultData));
        
        // Reference Range field
        const refRangeDataType = config.reference_range_data_type || 'textinput';
        const refRangeData = getDataValue('reference_range');
        fields.push(buildFieldByType('reference_range', 'Reference Range', refRangeDataType, refRangeData));
        
        // Units field
        const unitsDataType = config.units_data_type || 'textinput';
        const unitsData = getDataValue('units');
        fields.push(buildFieldByType('units', 'Units', unitsDataType, unitsData));
        
        // Flag field
        const flagDataType = config.flag_data_type || 'textinput';
        const flagData = getDataValue('flag');
        fields.push(buildFieldByType('flag', 'Flag', flagDataType, flagData));
        
        // Lab Message field
        const labMessageDataType = config.lab_message_data_type || 'textarea';
        const labMessageData = getDataValue('lab_message');
        fields.push(buildFieldByType('lab_message', 'Lab Message', labMessageDataType, labMessageData));
        
        return fields;
    };
    
    const buildFieldByType = (name, label, dataType, dataValue) => {
        // When dataType is 'none', show read-only '--' (no input needed)
        if (dataType === 'none') {
            return {
                type: 'TextInput',
                label: label,
                name: name,
                placeholder: '--',
                value: '--',
                disabled: true,
                readOnly: true,
            };
        }
        
        const options = Array.isArray(dataValue.options) ? dataValue.options : [];
        
        // Reference Range is always disabled/read-only
        const isDisabled = name === 'reference_range';
        
        switch (dataType) {
            case 'dropdown':
                const dropdownOptions = [
                    { label: '-Select-', value: '' },
                    ...options.map(opt => ({ label: opt, value: opt }))
                ];
                return {
                    type: 'SelectList',
                    options: dropdownOptions,
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                };
            case 'radio':
                const radioOptions = options.map(opt => ({ label: opt, value: opt }));
                return {
                    type: 'RadioGroup',
                    options: radioOptions,
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                };
            case 'checkbox':
                const checkboxOptions = options.map(opt => ({ label: opt, value: opt }));
                return {
                    type: 'Checkbox',
                    options: checkboxOptions,
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                };
            case 'textarea':
                return {
                    type: 'TextArea',
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                };
            case 'number':
                return {
                    type: 'TextInput',
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                    isDecimal: true, // Allow numbers including decimals
                };
            case 'textinput':
            default:
                return {
                    type: 'TextInput',
                    label: label,
                    name: name,
                    placeholder: label,
                    disabled: isDisabled,
                };
        }
    };
    
    // Update inputFields when laborderOptions loads and modal is open
    useEffect(() => {
        if (laborderOptions.length > 0 && isModalOpen && inputFields.length > 0) {
            // Update the lab_order field options in existing inputFields
            const updatedFields = inputFields.map(field => {
                if (field.name === 'lab_order' && field.type === 'SelectList') {
                    return {
                        ...field,
                        options: laborderOptions
                    };
                }
                return field;
            });
            setInputFields(updatedFields);
        } else if (laborderOptions.length > 0 && inputFields.length === 0) {
            // Initialize if fields are empty
            setInputFields(getDefaultInputFields(laborderOptions));
        }
    }, [laborderOptions.length, isModalOpen]);
    // ---- Inline Editing Functions ----
    
    // Find config matching a row's lab_order name
    const getConfigForRow = (rowData) => {
        const labOrderName = rowData?.lab_order || rowData?.data?.lab_order || '';
        return childLabConfigs.find(config => config.lab_order === labOrderName) || null;
    };
    
    // Handle inline field change
    const inlineChangeHandler = (rowIndex, fieldName, value) => {
        const updatedData = [...panelList.tableRows.data];
        updatedData[rowIndex] = {
            ...updatedData[rowIndex],
            data: {
                ...updatedData[rowIndex].data,
                [fieldName]: value
            }
        };
        setPanelList({ ...panelList, tableRows: { data: updatedData } });
        
        // Track modified row
        setModifiedRowIds(prev => {
            const next = new Set(prev);
            next.add(rowIndex);
            return next;
        });
        
        // Sync to parent state for new entries
        if (!parent_id) {
            if (setChildLab) setChildLab(updatedData);
            if (updateFormData) {
                const childLabPayload = updatedData.map(item => {
                    const data = item.data || item;
                    const { action, ...rest } = data;
                    return {
                        lab_order: rest.lab_order || '',
                        result: rest.result || '',
                        reference_range: rest.reference_range || '',
                        units: rest.units || '',
                        flag: rest.flag || '',
                        lab_message: rest.lab_message || ''
                    };
                });
                updateFormData({ child_lab: childLabPayload });
            }
        }
    };
    
    // Save a single row (for existing entries)
    const inlineSaveRow = async (rowIndex) => {
        if (!parent_id) return; // New entries auto-sync, no API call
        
        const rowData = panelList.tableRows.data[rowIndex];
        const data = rowData?.data || rowData;
        
        Loader(true);
        try {
            const payload = {
                parent_lab_id: parent_id,
                lab_order: data.lab_order || '',
                result: data.result || '',
                reference_range: data.reference_range || '',
                units: data.units || '',
                flag: data.flag || '',
                lab_message: data.lab_message || ''
            };
            
            let response;
            if (data.id) {
                response = await PatientChartsService.updateChildLabOrders(data.id, payload);
            } else {
                response = await PatientChartsService.saveChildLabOrders(payload);
            }
            
            if (response && response.status && response.status === "ERROR") {
                const errorMessage = getErrorMessage(response);
                Toast.error(errorMessage, 5000);
            } else {
                Toast.success(`"${data.lab_order}" saved successfully`);
                // Clear modified flag
                setModifiedRowIds(prev => {
                    const next = new Set(prev);
                    next.delete(rowIndex);
                    return next;
                });
                fetchPanelList();
                if (fetchRecords) fetchRecords();
            }
        } catch (error) {
            console.error('Error saving child lab order:', error);
            // Try to extract error message from error response
            const errorMessage = error?.response?.data?.data 
                ? getErrorMessage({ data: error.response.data.data, status: 'ERROR' })
                : (error?.response?.data?.message || error?.message || 'Failed to save');
            Toast.error(errorMessage, 5000);
        } finally {
            Loader(false);
        }
    };
    
    // Save all modified rows
    const inlineSaveAll = async () => {
        if (!parent_id) return;
        
        // Since we already checked parent_id exists, we can simplify this
        const rowsToSave = [...modifiedRowIds];
        if (rowsToSave.length === 0) {
            // Save all rows if none tracked as modified
            for (let i = 0; i < panelList.tableRows.data.length; i++) {
                rowsToSave.push(i);
            }
        }
        
        Loader(true);
        let savedCount = 0;
        let errorCount = 0;
        
        for (const rowIndex of rowsToSave) {
            const rowData = panelList.tableRows.data[rowIndex];
            const data = rowData?.data || rowData;
            
            try {
                const payload = {
                    parent_lab_id: parent_id,
                    lab_order: data.lab_order || '',
                    result: data.result || '',
                    reference_range: data.reference_range || '',
                    units: data.units || '',
                    flag: data.flag || '',
                    lab_message: data.lab_message || ''
                };
                
                let response;
                if (data.id) {
                    response = await PatientChartsService.updateChildLabOrders(data.id, payload);
                } else {
                    response = await PatientChartsService.saveChildLabOrders(payload);
                }
                
                if (response && response.status && response.status === "ERROR") {
                    errorCount++;
                    // Log the error for debugging (optional - can be removed)
                    console.error('Error saving row:', getErrorMessage(response));
                } else {
                    savedCount++;
                }
            } catch (error) {
                console.error('Error saving row:', error);
                errorCount++;
                // Log the error message for debugging
                const errorMessage = error?.response?.data?.data 
                    ? getErrorMessage({ data: error.response.data.data, status: 'ERROR' })
                    : (error?.response?.data?.message || error?.message || 'Failed to save');
                console.error('Error details:', errorMessage);
            }
        }
        
        Loader(false);
        setModifiedRowIds(new Set());
        
        if (errorCount > 0) {
            Toast.warning(`${savedCount} saved, ${errorCount} failed`);
        } else {
            Toast.success(`All ${savedCount} lab panel(s) saved successfully`);
        }
        
        fetchPanelList();
        if (fetchRecords) fetchRecords();
    };
    
    // Render the appropriate inline field based on config data type
    const renderInlineField = (fieldName, value, config, rowIndex) => {
        // When config says 'none' for this field, show -- (no input)
        const dataType = config ? (config[`${fieldName}_data_type`] || 'textinput') : null;
        if (config && dataType === 'none') {
            return <span className="text-sm text-muted">--</span>;
        }
        
        // Reference range is always read-only
        if (fieldName === 'reference_range') {
            return <span className="text-sm">{value || '-'}</span>;
        }
        
        if (!config) {
            return (
                <FormInputs
                    type="TextInput"
                    value={value || ''}
                    changeHandler={(val) => inlineChangeHandler(rowIndex, fieldName, val)}
                    className="form-control form-control-sm"
                    placeholder={fieldName.replace('_', ' ')}
                />
            );
        }
        
        const configDataType = config[`${fieldName}_data_type`] || 'textinput';
        const dataValue = config[`${fieldName}_data_value`] || {};
        const options = Array.isArray(dataValue.options) ? dataValue.options : [];
        
        switch (dataType) {
            case 'dropdown':
                return (
                    <FormInputs
                        type="SelectList"
                        options={[{ label: '-Select-', value: '' }, ...options.map(o => ({ label: o, value: o }))]}
                        value={value || ''}
                        changeHandler={(val) => inlineChangeHandler(rowIndex, fieldName, val)}
                        className="form-control form-control-sm"
                    />
                );
            case 'radio':
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {options.map((opt, optIdx) => (
                            <div className="form-check form-check-inline mb-0" key={optIdx}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name={`inline_${rowIndex}_${fieldName}`}
                                    id={`inline_${rowIndex}_${fieldName}_${optIdx}`}
                                    checked={value === opt}
                                    onChange={() => inlineChangeHandler(rowIndex, fieldName, opt)}
                                />
                                <label 
                                    className="form-check-label text-xs" 
                                    htmlFor={`inline_${rowIndex}_${fieldName}_${optIdx}`}
                                >
                                    {opt}
                                </label>
                            </div>
                        ))}
                    </div>
                );
            case 'checkbox': {
                const selectedValues = typeof value === 'string'
                    ? value.split(',').filter(v => v !== '')
                    : (Array.isArray(value) ? value : []);
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {options.map((opt, optIdx) => {
                            const isChecked = selectedValues.includes(opt);
                            return (
                                <div className="form-check form-check-inline mb-0" key={optIdx}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`inline_${rowIndex}_${fieldName}_${optIdx}`}
                                        checked={isChecked}
                                        onChange={(e) => {
                                            let newValues;
                                            if (e.target.checked) {
                                                newValues = [...selectedValues, opt];
                                            } else {
                                                newValues = selectedValues.filter(v => v !== opt);
                                            }
                                            inlineChangeHandler(rowIndex, fieldName, newValues.join(','));
                                        }}
                                    />
                                    <label 
                                        className="form-check-label text-xs"
                                        htmlFor={`inline_${rowIndex}_${fieldName}_${optIdx}`}
                                    >
                                        {opt}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                );
            }
            case 'textarea':
                return (
                    <FormInputs
                        type="TextArea"
                        value={value || ''}
                        changeHandler={(val) => inlineChangeHandler(rowIndex, fieldName, val)}
                        className="form-control form-control-sm"
                        style={{ minHeight: '34px', resize: 'vertical' }}
                        placeholder={fieldName.replace('_', ' ')}
                    />
                );
            case 'number':
                return (
                    <input
                        type="number"
                        step="any"
                        className="form-control form-control-sm"
                        value={value || ''}
                        onChange={(e) => inlineChangeHandler(rowIndex, fieldName, e.target.value)}
                        placeholder="#"
                    />
                );
            case 'textinput':
            default:
                return (
                    <FormInputs
                        type="TextInput"
                        value={value || ''}
                        changeHandler={(val) => inlineChangeHandler(rowIndex, fieldName, val)}
                        className="form-control form-control-sm"
                        placeholder={fieldName.replace('_', ' ')}
                    />
                );
        }
    };
    
    // Render the inline editable table
    const renderInlineEditTable = () => {
        const rows = panelList.tableRows.data;
        
        if (!rows || rows.length === 0) {
            return (
                <div className="text-center py-4 text-muted">
                    <i className="fas fa-flask mr-2"></i>
                    No child lab orders configured for this lab order
                </div>
            );
        }
        
        return (
            <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ width: '100%' }}>
                    <thead className="bg-light">
                        <tr>
                            <th className="text-xs text-uppercase" style={{ minWidth: '130px', width: '15%' }}>Lab Ordered</th>
                            <th className="text-xs text-uppercase" style={{ minWidth: '120px', width: '17%' }}>Result</th>
                            <th className="text-xs text-uppercase" style={{ minWidth: '110px', width: '14%' }}>Reference Range</th>
                            <th className="text-xs text-uppercase" style={{ minWidth: '100px', width: '14%' }}>Units</th>
                            <th className="text-xs text-uppercase" style={{ minWidth: '100px', width: '14%' }}>Flag</th>
                            <th className="text-xs text-uppercase" style={{ minWidth: '110px', width: '16%' }}>Lab Message</th>
                            <th className="text-xs text-uppercase text-center" style={{ minWidth: '70px', width: '10%' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => {
                            const data = row.data || row;
                            const config = getConfigForRow(data);
                            const isModified = modifiedRowIds.has(rowIndex);
                            
                            return (
                                <tr 
                                    key={row.rowId || rowIndex} 
                                    style={isModified ? { backgroundColor: '#fff9e6' } : {}}
                                >
                                    <td className="align-middle py-1 px-2">
                                        <span className="font-weight-bold text-sm">{data.lab_order || ''}</span>
                                    </td>
                                    <td className="align-middle py-1 px-2">
                                        {renderInlineField('result', data.result, config, rowIndex)}
                                    </td>
                                    <td className="align-middle py-1 px-2">
                                        {renderInlineField('reference_range', data.reference_range, config, rowIndex)}
                                    </td>
                                    <td className="align-middle py-1 px-2">
                                        {renderInlineField('units', data.units, config, rowIndex)}
                                    </td>
                                    <td className="align-middle py-1 px-2">
                                        {renderInlineField('flag', data.flag, config, rowIndex)}
                                    </td>
                                    <td className="align-middle py-1 px-2">
                                        {renderInlineField('lab_message', data.lab_message, config, rowIndex)}
                                    </td>
                                    <td className="align-middle py-1 px-2 text-center">
                                        <div className="d-flex justify-content-center align-items-center" style={{ gap: '4px' }}>
                                            {parent_id && (
                                                <Buttons
                                                    className={`btn btn-icon btn-transparent-dark ${isModified ? '' : 'opacity-50'}`}
                                                    iconType="Update"
                                                    title={isModified ? "Save this row" : "No changes to save"}
                                                    clickHandler={() => inlineSaveRow(rowIndex)}
                                                    disabled={!isModified}
                                                    acl="any"
                                                />
                                            )}
                                            <Buttons
                                                className="btn btn-icon btn-transparent-dark"
                                                iconType="Remove"
                                                title="Delete"
                                                clickHandler={() => deleteHandler(row.rowId || data.id)}
                                                isDelete={true}
                                                acl="any"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };
    
    const changeHandler = (fieldName, value, e) => {
        setFormData({ ...formData, [fieldName]: value });
        setValidation({ ...validation, [fieldName]: '' });
    }

    return (
        <>
        {isModalOpen===true &&
            <AddLabPanel 
                formData={formData}
                validation={validation}
                changeHandler={changeHandler}
                inputFields={inputFields}
                submiHandler={submiHandler} 
                toogleHandler={()=>{
                    setModalToogle(false);
                    setFormData({});
                    setSelectedConfig(null);
                    setInputFields(getDefaultInputFields(laborderOptions));
                }} />
        }
        
        <div className="sh_innerPortview">
            <div className="row">
                {/* <div className="col-12 col-lg-4">
                    <div className="sh_cardBox h-auto shadow-none border">
                        <div className="sh_cardBody p-3">
                            {
                                inputFields.map((itm, ind) => {
                                    return (
                                        <>
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="form-group mb-1">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <label className="text-sm text-truncate mb-0 opacity-75">{itm.label}</label></div>
                                                            <div className="col-12">
                                                                <FormInputs
                                                                    changeHandler={(value, e) => changeHandler(itm.name, value, e)}
                                                                    value={formData[itm.name]}
                                                                    {...itm}
                                                                />
                                                                {isSet(validation[itm.name], '') !== '' ? <span className="text-danger m-1">
                                                                    {isSet(validation[itm.name], '')}</span> : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>

                                        </>
                                    )
                                })
                            }

                        </div>
                        <div className="sh_cardFooter justify-content-center">
                            <button className="btn btn-primary btn-sm text-sm" onClick={submiHandler}> {formData.id ? 'Update' : 'Add'} </button>
                        </div>
                    </div>
                </div> */}
                <div className="col-12 col-lg-12">

                    <div className="sh_cardBox h-auto sh_labpanelht shadow-none border">
                        <div className="sh_cardHeader">
                            <div className="form-group d-flex mb-0 justify-content-between align-items-center">
                                <h6 className="text-md mb-0">
                                    Lab Panel List
                                    {hasConfiguredChildLabOrders && childLabConfigs.length > 0 && (
                                        <span className="badge bg-info text-white ml-2" style={{ fontSize: '10px', verticalAlign: 'middle' }}>
                                            Inline Edit
                                        </span>
                                    )}
                                </h6>
                                <div className="addAction d-flex justify-content-between align-items-center" style={{ gap: '8px' }}>
                                    {hasConfiguredChildLabOrders && parent_id && modifiedRowIds.size > 0 && (
                                        <button
                                            className="btn btn-primary btn-sm"
                                            onClick={inlineSaveAll}
                                            title="Save all modified rows"
                                            style={{ fontSize: '12px' }}
                                        >
                                            <i className="fas fa-save mr-1"></i>
                                            Save All ({modifiedRowIds.size})
                                        </button>
                                    )}
                                    {!hasConfiguredChildLabOrders && (
                                        <Buttons
                                            title={"Add Lab Panel"}
                                            className="btn btn-icon btn-transparent-dark mr-2"
                                            acl={"any"}
                                            iconType={"AddIconDetailView"}
                                            clickHandler={()=>{
                                                setFormData({});
                                                setSelectedConfig(null);
                                                setInputFields(getDefaultInputFields(laborderOptions));
                                                setModalToogle(true);
                                                setValidation({});
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="sh_cardBody p-0">
                            <SectionLoader isLoading={isLoading} />
                            {hasConfiguredChildLabOrders && childLabConfigs.length > 0
                                ? renderInlineEditTable()
                                : <TableGrid {...panelList} />
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
LabPanel.defaultProps={
    fetchRecords:()=>{}
}

export default WithRoutify(LabPanel);
